import { useEffect, useState } from "react";
import { Download, BarChart3, Clock, AlertTriangle, CheckCircle2, ListTodo } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api from "../lib/api";
import { Avatar } from "../lib/helpers";

const KPI = ({ icon: Icon, label, value, color, testid }) => (
  <div className="bg-[#121214] border border-white/10 rounded-xl p-4" data-testid={testid}>
    <Icon className="h-4 w-4 mb-2" style={{ color }} />
    <p className="font-display text-2xl font-extrabold">{value}</p>
    <p className="text-xs text-zinc-500">{label}</p>
  </div>
);

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/reports/overview?days=${days}`).then((r) => setData(r.data)).catch(() => toast.error("No se pudo cargar el informe"));
  }, [days]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/reports/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "tareas.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo exportar"); }
  };

  if (!data) return <div className="p-8 text-zinc-500">Cargando informes…</div>;
  const { kpis, workload, burndown, boards_summary } = data;

  return (
    <div className="h-screen overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold" data-testid="reports-title">Informes</h1>
            <p className="text-sm text-zinc-500 mt-1">KPIs, productividad y tiempo invertido.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(+e.target.value)} data-testid="reports-range"
              className="bg-[#121214] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary text-zinc-300">
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
            <button onClick={exportCsv} data-testid="export-csv-button"
              className="bg-primary hover:bg-[#FF5047] text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors">
              <Download className="h-4 w-4" /> Exportar CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KPI icon={ListTodo} label="Tareas totales" value={kpis.total_tasks} color="#00B0FF" testid="kpi-total" />
          <KPI icon={CheckCircle2} label="Completadas" value={kpis.completed} color="#00E676" testid="kpi-completed" />
          <KPI icon={BarChart3} label="% completado" value={`${kpis.completion_rate}%`} color="#AB47BC" testid="kpi-rate" />
          <KPI icon={AlertTriangle} label="Vencidas" value={kpis.overdue} color="#FF1744" testid="kpi-overdue" />
          <KPI icon={Clock} label="Horas estimadas" value={kpis.estimated_hours} color="#FFD600" testid="kpi-estimated" />
          <KPI icon={Clock} label="Horas registradas" value={kpis.logged_hours} color="#FF6D00" testid="kpi-logged" />
        </div>

        <div className="bg-[#121214] border border-white/10 rounded-xl p-5">
          <h2 className="font-display font-bold mb-4">Tareas completadas por día</h2>
          <div className="h-56" data-testid="burndown-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={burndown}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fill: "#71717A", fontSize: 10 }} width={24} />
                <Tooltip contentStyle={{ background: "#1C1C1F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#fff" }} />
                <Bar dataKey="completadas" fill="#00E676" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#121214] border border-white/10 rounded-xl p-5" data-testid="workload-section">
          <h2 className="font-display font-bold mb-4">Carga de trabajo por persona</h2>
          {workload.length === 0 && <p className="text-sm text-zinc-600">Sin tareas asignadas todavía.</p>}
          <div className="space-y-3">
            {workload.map((w, wi) => {
              const maxOpen = Math.max(...workload.map((x) => x.open), 1);
              return (
                <div key={w.user.id} className="flex items-center gap-3 animate-fade-up" style={{ animationDelay: `${Math.min(wi * 50, 450)}ms` }} data-testid={`workload-${w.user.id}`}>
                  <Avatar user={w.user} size={32} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold truncate">{w.user.name}</span>
                      <span className="text-xs text-zinc-500">
                        {w.open} pendientes · {w.done} hechas{w.overdue > 0 && <span className="text-red-400"> · {w.overdue} vencidas</span>} · {Math.round(w.seconds / 360) / 10} h registradas
                      </span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden mt-1.5">
                      <div className="h-full bg-[#00B0FF] rounded-full" style={{ width: `${(w.open / maxOpen) * 100}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-[#121214] border border-white/10 rounded-xl p-5" data-testid="boards-summary-section">
          <h2 className="font-display font-bold mb-4">Por tablero / cliente</h2>
          <div className="space-y-2">
            {boards_summary.map((b, i) => {
              const pct = b.total ? Math.round((b.done / b.total) * 100) : 0;
              return (
                <div key={b.id || `${b.name}-${i}`} className="flex items-center gap-3 animate-fade-up" style={{ animationDelay: `${Math.min(i * 50, 450)}ms` }}>
                  <span className="text-sm font-medium flex-1 truncate">{b.name}{b.client && <span className="text-xs text-[#00B0FF] ml-2">({b.client})</span>}</span>
                  <span className="text-xs text-zinc-500 w-28 text-right">{b.done}/{b.total} · {pct}%</span>
                  <div className="w-40 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-[#00E676] rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
